import type { Translations } from "./types"

export const en: Translations = {
	appName: "zPlanem",

	hero: {
		sentences: [
			"From plan to execution",
			"Stress-free",
			"Always up to date",
			"AI support every step of the way",
		],
	},

	language: {
		label: "Language",
		pl: "Polski",
		en: "English",
		uk: "Українська",
	},

	common: {
		loading: "Loading...",
	},

	auth: {
		login: "Login",
		logout: "Logout",
		googleLogin: "Continue with Google",
		getStarted: "Get Started",
		backToMain: "Back to main page",
	},

	dashboard: {
		title: "Dashboard",
		welcome: "Welcome back!",
		dummyText: "Your properties and progress will appear here.",
	},

	properties: {
		newPropertyCta: "+ New property",
		addFirstPropertyCta: "Add your first property",
		emptyStateTitle: "No properties yet",
		emptyStateBody:
			"Create your first property to start planning your renovation.",
		lastOpenedLabel: "Last opened",
		propertiesListHeading: "Your properties",
		types: {
			apartment: "Apartment",
			house: "House",
			office: "Office",
			other: "Other",
		},
		tabs: {
			navLabel: "Property sections",
			overview: "Overview",
			projects: "Projects",
			tickets: "Tasks",
			journal: "Journal",
			documents: "Documents",
			budget: "Budget",
		},
		form: {
			newPageTitle: "New property",
			nameLabel: "Name",
			namePlaceholder: "e.g. Mokotowska 12",
			typeLabel: "Type",
			customTypeLabel: "Describe the type",
			customTypePlaceholder: "e.g. garage, cottage",
			descriptionLabel: "Description",
			descriptionPlaceholder: "Optional notes about this property",
			submit: "Create property",
			cancel: "Cancel",
			errors: {
				nameRequired: "Name is required.",
				nameTooLong: "Name must be at most {max} characters.",
				typeRequired: "Pick a type.",
				customTypeRequired: "Describe the type.",
				customTypeTooLong: "Custom type must be at most {max} characters.",
				descriptionTooLong: "Description must be at most {max} characters.",
				unknown: "Something went wrong. Please try again.",
			},
		},
		detail: {
			breadcrumbsNavLabel: "Breadcrumbs",
			manageLabel: "Manage",
			manageMenu: {
				edit: "Edit property",
				transfer: "Transfer ownership",
			},
		},
		edit: {
			title: 'Edit "{propertyName}"',
			nameLabel: "Name",
			descriptionLabel: "Description",
			save: "Save changes",
			saving: "Saving...",
			cancel: "Cancel",
			readOnlyNotice:
				"Only the owner or architect members can edit this property.",
			improveWithAi: "Improve with AI",
			addDescription: "Add a description…",
			editName: "Edit name",
			editDescription: "Edit description",
			descriptionPlaceholder: "Describe this property in Markdown…",
			deleteSectionTitle: "Delete property",
			deleteSectionBody:
				"This removes the property from your account. It cannot be undone.",
			deleteAction: "Delete property",
			deleteConfirmTitle: "Delete property?",
			deleteConfirmBody:
				'"{propertyName}" will disappear from your account. This cannot be undone.',
			deleteConfirmAction: "Delete permanently",
			deleteError: "Could not delete the property.",
		},
		transfer: {
			title: 'Transfer ownership of "{propertyName}"',
			explainer:
				"Pick a member of this property to become the new owner. You'll keep access as a member with your current role. This cannot be undone from the new owner's side without their action.",
			memberLabel: "Member",
			emptyState:
				"No members to transfer to. Invite someone first from the property page.",
			confirmationLabel: "Type the property name to confirm",
			confirmationPlaceholder: "{propertyName}",
			submit: "Transfer ownership to {name}",
			submitting: "Transferring...",
			cancel: "Cancel",
		},
		share: {
			buttonLabel: "Share",
			or: "Or",
			modalTitle: 'Share "{propertyName}"',
			invite: {
				emailLabel: "Email",
				emailPlaceholder: "name@example.com",
				roleLabel: "Role",
				submit: "Send invitation",
				submitting: "Sending...",
				confirm: {
					title: "Confirm invitation",
					body: "Invite {email} as {role}?",
					send: "Send invitation",
					cancel: "Cancel",
				},
				errors: {
					emailInvalid: "Enter a valid email address.",
					selfInvite: "You can't invite yourself — you're the owner.",
					alreadyMember: "{email} is already a member.",
					unknown: "Couldn't send the invitation. Please try again.",
				},
			},
			members: {
				heading: "Members",
				ownerLabel: "Owner",
				remove: {
					button: "Remove member",
					submitting: "Removing...",
					confirm: {
						title: "Remove member",
						body: "Remove {name} from this property?",
						remove: "Remove",
						cancel: "Cancel",
					},
					errors: {
						unknown: "Couldn't remove the member.",
					},
				},
				role: {
					button: "Change member role",
					errors: {
						unknown: "Couldn't change the member role.",
					},
				},
			},
			invitations: {
				heading: "Pending invitations",
				emptyState: "No pending invitations.",
				expiresIn: "Expires in {days} days",
				expiresToday: "Expires today",
				menu: {
					actionsLabel: "Invitation actions",
				},
				remove: {
					button: "Remove invitation",
					submitting: "Removing...",
					confirm: {
						title: "Remove invitation",
						body: "Cancel invitation for {name}?",
						remove: "Remove invitation",
						cancel: "Cancel",
					},
					errors: {
						unknown: "Couldn't remove the invitation.",
					},
				},
				resend: {
					button: "Resend",
					submitting: "Resending…",
					confirm: {
						title: "Resend invitation",
						body: "Resend invitation to {email}?",
						send: "Resend",
						cancel: "Cancel",
					},
					errors: {
						unknown: "Couldn't resend the invitation.",
					},
				},
				role: {
					button: "Change invitation role",
					errors: {
						unknown: "Couldn't change the invitation role.",
					},
				},
			},
			roles: {
				member: "Member",
				contractor: "Contractor",
				architect: "Architect",
			},
			roleDescriptions: {
				member: "General participant — kept in the loop on this property.",
				contractor:
					"A worker performing renovation or construction tasks on this property.",
				architect:
					"A designer responsible for planning and design decisions for this property.",
			},
			qr: {
				generateButton: "Generate QR",
				dialogTitle: "Invitation QR — {role}",
				pinLabel: "PIN",
				instructions:
					"Have the invitee scan this code in their zPlanem app, then enter the PIN.",
				instructionsNoPin:
					"Have the invitee scan this code in their zPlanem app — you will approve their request here.",
				expiresInMmSs: "Expires in {mm}:{ss}",
				expired: "Expired",
				done: "Done",
				generating: "Generating...",
				error: "Couldn't generate the QR invitation. Please try again.",
			},
		},
	},

	projects: {
		list: {
			heading: "Projects",
			createCta: "New project",
			emptyStateTitle: "No projects yet",
			emptyStateBody: "Create the first project for this property.",
			archivedHeading: "Archived",
			archivedBadge: "Archived",
		},
		statuses: {
			draft: "Draft",
			ready_for_work: "Ready for work",
			in_progress: "In progress",
			awaiting_acceptance: "Awaiting acceptance",
			accepted: "Accepted",
		},
		types: {
			garden: "Garden",
			construction: "Construction",
			interior: "Interior",
		},
		tabs: {
			navLabel: "Project sections",
			details: "Details",
			tasks: "Tasks",
			visualizations: "Visualizations & Plans",
			visualizationsShort: "Visuals",
			inspirations: "Inspirations",
			documents: "Documents",
			budget: "Budget",
			members: "Members",
		},
		form: {
			createTitle: "New project",
			nameLabel: "Name",
			namePlaceholder: "e.g. Garden makeover",
			typeLabel: "Type",
			descriptionLabel: "Description",
			descriptionPlaceholder: "Optional notes about the project",
			submit: "Create project",
			cancel: "Cancel",
			errors: {
				nameRequired: "Name is required",
				nameTooLong: "Name can be at most {max} characters",
				typeRequired: "Pick a project type",
				descriptionTooLong: "Description can be at most {max} characters",
				costInvalid: "Enter a valid amount (e.g. 12500.00)",
				unknown: "Something went wrong. Try again.",
			},
		},
		detail: {
			statusLabel: "Status",
			acceptedOwnerOnlyHint:
				"Only the property owner can mark a project as accepted",
			membersHeading: "Members",
			addMemberCta: "Add member",
			addMemberTitle: "Add a project member",
			addMemberEmpty: "Everyone eligible is already in this project",
			removeMemberLabel: "Remove",
			archiveCta: "Archive",
			unarchiveCta: "Unarchive",
			archivedNotice: "This project is archived",
			editCta: "Edit",
			editTitle: "Edit project",
			save: "Save",
			saving: "Saving…",
			cancel: "Cancel",
			estimatedExecutionCostLabel: "Estimated execution cost",
			costPlaceholder: "e.g. 12500.00",
			currencyLabel: "Currency",
			noDescription: "No description yet",
			descriptionHeading: "Description",
			journalHeading: "Journal",
		},
		cost: {
			heading: "Cost of creating the project",
			noCost: "No cost proposed yet",
			acceptedLabel: "Accepted cost",
			pendingLabel: "Pending proposal",
			proposeCta: "Propose cost",
			proposeTitle: "Propose a cost",
			amountLabel: "Amount",
			amountPlaceholder: "e.g. 12500.00",
			currencyLabel: "Currency",
			submit: "Propose",
			cancel: "Cancel",
			acceptCta: "Accept",
			rejectCta: "Reject",
			markPaidCta: "Mark as paid",
			markPaidHint: "Available after a cost is accepted",
			paidBadge: "Paid",
			errors: {
				amountInvalid: "Enter a valid amount (e.g. 12500.00)",
				unknown: "Something went wrong. Try again.",
			},
		},
		visualizations: {
			emptyTitle: "No visualizations yet",
			emptyBody:
				"Renderings, drawings and plans for this project will appear here.",
			addCta: "Add visualization",
			processing: "Processing…",
			modalTitle: "Add visualization",
			tabFile: "Upload file",
			tabLink: "Add link",
			chooseFile: "Choose file",
			dropHere: "Drag files here, or click to browse",
			fileHint: "Images, PDFs or videos — up to 25 MB per file.",
			urlLabel: "Link",
			urlPlaceholder: "https://…",
			titleLabel: "Title (optional)",
			descriptionLabel: "Description (optional)",
			add: "Add",
			adding: "Adding…",
			cancel: "Cancel",
			download: "Download",
			delete: "Delete",
			deleteTitle: "Delete visualization?",
			deleteBody: "This removes it permanently and can't be undone.",
			close: "Close",
			errorUnsupported: "That file type isn't supported.",
			errorTooLarge: "That file is larger than the 25 MB limit.",
			errorUpload: "Couldn't add the visualization. Please try again.",
			errorLoad: "Couldn't load visualizations.",
			uploadedByLabel: "Uploaded by {name}",
		},
		inspirations: {
			emptyTitle: "No inspirations yet",
			emptyBody: "Collect references and ideas for this project here.",
			addCta: "Add inspiration",
			processing: "Processing…",
			modalTitle: "Add inspiration",
			tabFile: "Upload file",
			tabLink: "Add link",
			chooseFile: "Choose file",
			dropHere: "Drag files here, or click to browse",
			fileHint: "Images, PDFs or videos — up to 5 MB per file.",
			urlLabel: "Link",
			urlPlaceholder: "https://…",
			titleLabel: "Title (optional)",
			descriptionLabel: "Description (optional)",
			add: "Add",
			adding: "Adding…",
			cancel: "Cancel",
			download: "Download",
			delete: "Delete",
			deleteTitle: "Delete inspiration?",
			deleteBody: "This removes it permanently and can't be undone.",
			close: "Close",
			errorUnsupported: "That file type isn't supported.",
			errorTooLarge: "That file is larger than the 5 MB limit.",
			errorUpload: "Couldn't add the inspiration. Please try again.",
			errorLoad: "Couldn't load inspirations.",
			uploadedByLabel: "Uploaded by {name}",
		},
		mediaUpload: {
			filesSelected: {
				one: "{count} file selected",
				few: "{count} files selected",
				many: "{count} files selected",
				other: "{count} files selected",
			},
			addFiles: {
				one: "Add {count} file",
				few: "Add {count} files",
				many: "Add {count} files",
				other: "Add {count} files",
			},
			addingFiles: {
				one: "Adding {count} file…",
				few: "Adding {count} files…",
				many: "Adding {count} files…",
				other: "Adding {count} files…",
			},
			filesSkipped: {
				one: "{count} file was skipped",
				few: "{count} files were skipped",
				many: "{count} files were skipped",
				other: "{count} files were skipped",
			},
			filesFailed: {
				one: "{count} file couldn’t be added",
				few: "{count} files couldn’t be added",
				many: "{count} files couldn’t be added",
				other: "{count} files couldn’t be added",
			},
			addAnother: "Add another file",
			removeFile: "Remove {name}",
			retryFailed: "Retry failed",
			statusUploading: "Uploading…",
			statusDone: "Added",
			statusFailed: "Failed",
			errorDuplicate: "Already in the list.",
			errorBatchLimit: "You can add up to {max} files at once.",
			errorQuotaFull: "This project is at its limit of {max} items.",
		},
		budget: {
			title: "Budget",
			spentLabel: "Confirmed spend",
			excludedNote: "{count} without an exchange rate — total is partial",
			draftsLabel: "to review",
			emptyTitle: "No expenses yet",
			emptyBody:
				"Record what you actually spent — add an expense by hand or capture an invoice and let Mareczek read it.",
			addCta: "Add expense",
			captureCta: "Capture invoice",
			formTitleAdd: "Add expense",
			formTitleEdit: "Edit expense",
			amountLabel: "Amount",
			currencyLabel: "Currency",
			kindLabel: "Category",
			kindNone: "No category",
			vendorLabel: "Vendor",
			descriptionLabel: "Description",
			occurredAtLabel: "Date",
			projectLabel: "Project",
			projectNone: "Whole property",
			save: "Save",
			cancel: "Cancel",
			amountInvalid: "Enter a valid amount (e.g. 1250.50)",
			kinds: {
				material: "Materials",
				labor: "Labor",
				service: "Services",
				equipment: "Equipment",
				mix: "Mixed",
				other: "Other",
			},
			statuses: {
				ai_draft: "Draft",
				confirmed: "Confirmed",
				rejected: "Rejected",
			},
			accept: "Accept",
			reject: "Reject",
			edit: "Edit",
			delete: "Delete",
			deleteConfirmBody: "Delete this expense?",
			captureModalTitle: "Capture invoice",
			captureHint:
				"A PDF or a photo of the invoice/receipt. Mareczek reads the amount, vendor and date — you review before it counts.",
			fileLabel: "Invoice file (PDF or photo)",
			capture: "Upload",
			capturing: "Uploading…",
			duplicateWarning: "Looks like this invoice was already captured.",
			extractionPending: "Reading the invoice…",
			proofLabel: "Invoice",
			errorLoad: "Could not load the budget.",
			errorSave: "Could not save the expense.",
			errorFileTooLarge: "This file is too large.",
		},
		documents: {
			emptyTitle: "No documents yet",
			emptyBody: "Upload PDFs, spreadsheets, or other files for this project.",
			emptyBodyProperty:
				"Upload PDFs, spreadsheets, or other files for this property.",
			addCta: "Add document",
			uploadModalTitle: "Upload document",
			titleLabel: "Title",
			titlePlaceholder: "e.g. Contract",
			fileLabel: "File",
			aiReadableLabel: "Let Mareczek (AI) read this",
			upload: "Upload",
			uploading: "Uploading…",
			cancel: "Cancel",
			delete: "Delete",
			deleteTitle: "Delete this document?",
			deleteBody:
				"This permanently deletes \u201c{title}\u201d \u2014 it cannot be undone.",
			deleteConfirm: "Delete document",
			download: "Download",
			open: "Open",
			scopeProject: "Project",
			scopeProperty: "Property",
			allInProperty: "All documents in the property",
			scopeFieldLabel: "Assign to",
			documentTypeLabel: "Document type",
			documentTypeOptions: {
				contract: "Contract",
				agreement: "Agreement",
				notice: "Notice",
				bill: "Bill",
				land_report: "Land report",
				other: "Other",
			},
			fileDropHint: "Drag a file here, or click to browse",
			grantsModalTitle: "Share document",
			grantsCta: "Share",
			ownerLabel: "Owner",
			grantToggleGrant: "Grant access",
			grantToggleRevoke: "Revoke access",
			errorLoad: "Could not load documents.",
			errorFileTooLarge: "This file exceeds the 50 MB limit.",
			errorUpload: "Upload failed.",
			editCta: "Edit document",
			editModalTitle: "Edit document",
			save: "Save",
			errorDownload: "Could not open the file. Try again.",
			errorDownloadBlocked: "Allow pop-ups for this site to open documents.",
			aiReadableHint:
				"Mareczek reviews documents marked as Contract (risks, missing protections, potentially abusive clauses) or Land report (parcel constraints and risks); other document types aren't reviewed.",
			aiUploadNoticeUnsupported:
				"AI can read PDF and image files only — this file will upload, but Mareczek won't process it.",
			aiUploadNoticeTooLarge:
				"This file is over the {size} AI-review limit — it will upload, but Mareczek won't review it.",
			aiEditNoticeTooLarge:
				"This file is over the {size} AI-review limit, so Mareczek won't review it.",
			aiBadgeReviewing: "Reviewing",
			aiBadgeReviewingHint: "Mareczek is reviewing this document.",
			aiBadgeReview: "AI review",
			aiBadgeReviewHint: "View Mareczek's review.",
			aiBadgeFailed: "Review failed",
			aiBadgeFailedHint:
				"Mareczek couldn't finish this review. Open for details.",
			aiBadgeUnsupported: "Unsupported file",
			aiBadgeUnsupportedHint: "AI can read PDF and image files only.",
			aiBadgeTooLarge: "Too large",
			aiBadgeTooLargeHint:
				"This file is too large for AI review ({size} limit).",
			aiReviewModalTitle: "Mareczek's document review",
			aiReviewProcessing: "Mareczek is reviewing this document",
			aiReviewFailed:
				"Mareczek couldn't complete this review. Toggle AI review off and on, or re-upload the file, to try again.",
			aiReviewFailedNotContract:
				"This document doesn't look like a contract, so there's nothing to review.",
			aiReviewFailedNotLandReport:
				"This document doesn't look like a land report, so there's nothing to review.",
			aiReviewFailedUnreadable:
				"Mareczek couldn't read this document. It may be scanned, encrypted, or corrupted.",
			aiReviewSummary: "Summary",
			aiReviewCritical: "Critical issues",
			aiReviewAdvantages: "In your favor",
			aiReviewDisadvantages: "Against you",
			aiReviewMissing: "Missing protections",
			aiReviewPossible: "Worth clarifying",
			aiReviewAbusive: "Potentially abusive clauses",
			aiReviewNoFindings: "Nothing flagged here.",
			aiReviewDisclaimer:
				"Mareczek is an AI assistant, not a lawyer. Double-check anything important before acting on it.",
			close: "Close",
		},
	},

	inviteEmail: {
		subject: "You've been invited to {propertyName} on zPlanem",
		greeting: "Hi,",
		body: "{inviterName} invited you to collaborate on {propertyName}. Click the link below to accept the invitation.",
		cta: "Accept invitation",
		signature: "— The zPlanem team",
		expiryNote: "This invitation expires in {days} days.",
	},

	mentionEmail: {
		subject: "{byDisplayName} mentioned you on {propertyName}",
		greeting: "Hi,",
		body: "{byDisplayName} mentioned you in the chat of {propertyName} and you haven't seen it yet. Open the chat to catch up.",
		cta: "Open the chat",
		signature: "— The zPlanem team",
	},

	invitationAcceptedEmail: {
		subject: "{accepterName} joined {propertyName}",
		greeting: "Hi,",
		body: "{accepterName} accepted your invitation to {propertyName} as {role}.",
		signature: "— The zPlanem team",
	},

	activityDigestEmail: {
		subject: "zPlanem — activity summary for {date}",
		intro: "Here's what changed in your properties since yesterday, 8:00:",
		openJournal: "Open the journal",
	},

	documentSharedEmail: {
		granteeSubject: "{actor} shared a document with you: {title}",
		granteeBody: "{actor} shared the document \u201c{title}\u201d with you.",
		ownerSubject: "Your document was shared: {title}",
		ownerBody:
			"{actor} shared your document \u201c{title}\u201d with {grantee}.",
		cta: "Open documents",
	},

	acceptInvitation: {
		title: "{inviterName} invited you to {propertyName} as {role}",
		loadingState: "Loading invitation...",
		terminalErrors: {
			notFound: "This invitation link is no longer valid.",
			expired:
				"This invitation has expired. Ask the property owner to send a new one.",
			alreadyAccepted: "This invitation was already accepted.",
		},
		emailMismatch:
			"This invitation was sent to {invitationEmail}. Sign out and sign in with that email to accept.",
		signInPrompt: "Sign in with {invitationEmail} to accept",
		accept: "Accept invitation",
		accepting: "Accepting...",
		success: "You've joined {propertyName}!",
		backToDashboard: "Go to dashboard",
		goToApp: "Go to app",
		unknownInviter: "Someone",
		qrOpenInAppTitle:
			"Open the zPlanem app on your phone to join {propertyName}",
		qrOpenInAppBody:
			"This QR invitation is delivered in person — finish accepting it by scanning the QR code in the zPlanem mobile app.",
	},

	aiIndicator: {
		processing: "AI processing",
		processed: "AI Processed",
		failed: "AI Failed",
	},

	notFound: {
		title: "This page does not exist",
	},

	chat: {
		headerName: "Mareczek",
		headerSubtitle: "AI assistant",
		inputPlaceholder: "Message Mareczek…",
		emptyState: "Start a conversation",
		sendButtonLabel: "Send message",
		openButtonLabel: "Open chat",
		minimizeButtonLabel: "Minimize chat",
		expandButtonLabel: "Expand chat",
		collapseButtonLabel: "Collapse chat",
		thinkingLabel: "Mareczek is working…",
		unreadBadge: {
			cap: "99+",
			label: "{count} unread messages",
		},
		shared: {
			headerName: "Members chat",
			propertyHeaderName: "Property chat",
			projectHeaderName: "Project chat",
			composerPlaceholder: "Write a message…",
			mentionSuggestionsLabel: "Mention suggestions",
			emptyState: "No messages yet.",
			sendButtonLabel: "Send",
			closeLabel: "Close members chat",
			memberCountSingular: "{count} member",
			memberCountPlural: "{count} members",
			deletedUser: "Deleted user",
		},
		errors: {
			sendFailed: "Couldn't send your message. Please try again.",
			loadFailed: "Couldn't load messages. Please try again.",
		},
		mention: {
			toastTitle: "{name} mentioned you",
		},
	},

	mareczek: {
		greetings: {
			propertyCreated:
				"Hi {ownerName}! I'm Mareczek, your zPlanem AI assistant. I'm here to help you plan and manage {propertyName} — drop me a message any time.",
			propertyJoined:
				"Hi {memberName}! Welcome to {propertyName}. I'm Mareczek, your zPlanem AI assistant — drop me a message any time.",
		},
		systemMessages: {
			memberJoined: "{fullName} has joined the property as {role}.",
		},
		agentActions: {
			executed: "Done — the action was completed.",
			executedSummary: "Done — {summary}.",
			failed: "The action couldn't be completed.",
			declined: "Action dismissed.",
			chainFailed:
				"Something went wrong on my side — I couldn't finish this reply.",
			card: {
				titles: {
					"create-project": "Create project",
					"update-project": "Update project",
					"propose-cost": "Propose cost",
					"archive-project": "Archive project",
					"invite-member": "Invite to property",
					"resend-invitation": "Resend invitation",
					"create-ticket": "Create ticket",
					"update-ticket": "Update ticket",
					"assign-ticket": "Assign ticket",
					"set-ticket-status": "Change ticket status",
					"set-ticket-reminder": "Set ticket reminder",
					"add-ticket-comment": "Add ticket comment",
					"delete-ticket": "Delete ticket",
					"delete-ticket-comment": "Delete ticket comment",
					"remove-ticket-attachment": "Remove ticket attachment",
					"rename-property": "Rename property",
					"update-property-description": "Update property description",
					"create-inspiration-from-url": "Add inspiration from link",
					"create-visualization-from-url": "Add visualization from link",
					"create-budget-entry": "Record expense",
					"update-budget-entry": "Update expense",
					"delete-budget-entry": "Delete expense",
					generic: "Proposed action",
				},
				accept: "Approve",
				decline: "Decline",
				statusAccepted: "Approved",
				statusDeclined: "Declined",
				statusExpired: "Expired",
				statusAwaitingOwner: "Waiting for the requester's approval",
			},
		},
	},

	notificationsBell: {
		title: "Notifications",
		close: "Close",
		empty: "Nothing new",
		markAllRead: "Mark all as read",
		chatMention: "{actor} mentioned you in a chat",
		ticketAssigned: "{actor} assigned you the ticket \u201c{title}\u201d",
		joinRequestCreated: "{actor} asked to join your property",
		agentActionPending: "Mareczek prepared an action awaiting your approval",
	},

	mediaModal: {
		prev: "Previous item",
		next: "Next item",
	},

	journal: {
		emptyTitle: "No activity yet",
		emptyDescription: "Events will appear here as they happen.",
		errorTitle: "Couldn't load journal",
		errorRetry: "Try again",
		deletedUser: "Deleted user",
		sourceProperty: "Property",
		events: {
			propertyCreated: {
				label: "Property created",
				aria: "Property created by {actor}",
			},
			memberInvitedEmail: {
				label: "Invitation sent (email)",
				aria: "{actor} sent an email invitation",
			},
			memberInvitedQr: {
				label: "QR invitation generated",
				aria: "{actor} generated a QR invitation",
			},
			memberJoined: {
				label: "Joined as {role}",
				aria: "{actor} joined as {role}",
			},
			unknown: {
				label: "Activity",
				aria: "Activity recorded",
			},
			projectCreated: {
				label: "Project created",
				aria: "{actor} created the project",
			},
			projectStatusChanged: {
				label: "Status: {from} → {to}",
				aria: "{actor} changed status from {from} to {to}",
			},
			projectCostProposed: {
				label: "Cost proposed: {amount} {currency}",
				aria: "{actor} proposed a cost of {amount} {currency}",
			},
			projectCostAccepted: {
				label: "Cost accepted: {amount} {currency}",
				aria: "{actor} accepted the cost of {amount} {currency}",
			},
			projectCostRejected: {
				label: "Cost rejected: {amount} {currency}",
				aria: "{actor} rejected the cost of {amount} {currency}",
			},
			projectPaid: {
				label: "Marked as paid: {amount} {currency}",
				aria: "{actor} marked the project as paid ({amount} {currency})",
			},
			projectMemberAdded: {
				label: "Member added: {target}",
				aria: "{actor} added {target} to the project",
			},
			projectMemberRemoved: {
				label: "Member removed: {target}",
				aria: "{actor} removed {target} from the project",
			},
			projectArchived: {
				label: "Project archived",
				aria: "{actor} archived the project",
			},
			projectUnarchived: {
				label: "Project restored",
				aria: "{actor} restored the project",
			},
			inspirationCreated: {
				label: "Inspiration added",
				aria: "{actor} added an inspiration",
			},
			inspirationDeleted: {
				label: "Inspiration removed",
				aria: "{actor} removed an inspiration",
			},
			visualizationCreated: {
				label: "Visualization added",
				aria: "{actor} added a visualization",
			},
			visualizationDeleted: {
				label: "Visualization removed",
				aria: "{actor} removed a visualization",
			},
			documentCreated: {
				label: "Document added",
				aria: "{actor} added a document",
			},
			documentDeleted: {
				label: "Document removed",
				aria: "{actor} removed a document",
			},
			documentUpdated: {
				label: "Document updated",
				aria: "{actor} updated a document",
			},
			ticketCreated: {
				label: "Task added: {title}",
				aria: "{actor} added a task: {title}",
			},
			ticketCreatedDefect: {
				label: "Defect reported: {title}",
				aria: "{actor} reported a defect: {title}",
			},
			ticketStatusChanged: {
				label: "{title}: {from} → {to}",
				aria: "{actor} moved {title} from {from} to {to}",
			},
			ticketAssigned: {
				label: "{title} → {assignee}",
				aria: "{actor} assigned {title} to {assignee}",
			},
			ticketUnassigned: {
				label: "{title}: unassigned",
				aria: "{actor} unassigned {title}",
			},
		},
	},

	tickets: {
		emptyTitle: "No tasks yet",
		emptyDescription: "Tasks will appear here as they are added.",
		emptyFilteredTitle: "No tasks match these filters",
		emptyFilteredDescription: "Clear filters to see all tasks in this scope.",
		errorTitle: "Couldn't load tasks",
		errorRetry: "Try again",
		createCta: "Add task",
		createTitle: "New task",
		titleLabel: "Title",
		titlePlaceholder: "e.g. Order tiles",
		kindLabel: "Type",
		assigneeSelectLabel: "Assignee",
		submitCreate: "Add",
		creating: "Adding...",
		statusLabel: "Status",
		assignChangeError: "Couldn't change assignee",
		statusChangeError: "Couldn't change status",
		loadMore: "Load more",
		loadingMore: "Loading...",
		boardAriaLabel: "Task board",
		viewSwitcherLabel: "Task view",
		viewBoard: "Board",
		viewList: "List",
		listAriaLabel: "Task list",
		unassigned: "Unassigned",
		dueLabel: "Due",
		createdLabel: "Created",
		updatedLabel: "Updated",
		assigneeLabel: "Assignee",
		reminderLabel: "Reminder",
		reminderSave: "Save",
		reminderClear: "Clear",
		reminderChangeError: "Couldn't change reminder",
		descriptionTitle: "Description",
		noDescription: "No description",
		backToList: "Back to tasks",
		backToMyTasks: "Back to my tasks",
		backToProjectTasks: "Back to project tasks",
		attachmentsTitle: "Attachments",
		noAttachments: "No attachments",
		attachmentUpload: "Add file",
		attachmentUploading: "Uploading...",
		attachmentOpen: "Open attachment",
		attachmentDelete: "Delete",
		attachmentUploadError: "Couldn't add attachment",
		attachmentDeleteError: "Couldn't delete attachment",
		commentsTitle: "Comments",
		commentsEmpty: "No comments",
		commentPlaceholder: "Add a comment",
		commentSubmit: "Add comment",
		commentSubmitting: "Adding...",
		commentCreateError: "Couldn't add comment",
		commentEdit: "Edit",
		commentSave: "Save",
		commentCancel: "Cancel",
		commentEditError: "Couldn't update comment",
		commentDelete: "Delete",
		commentDeleteError: "Couldn't delete comment",
		commentEdited: "edited",
		mentionSuggestionsLabel: "Mention suggestions",
		myTicketsTitle: "My tasks",
		myTicketsEmptyTitle: "No tasks assigned to or created by you",
		myTicketsEmptyDescription:
			"Tasks assigned to you or created by you will appear here.",
		myTicketsFilteredEmptyTitle: "No tasks match these filters",
		myTicketsFilteredEmptyDescription:
			"Clear filters to see all tasks assigned to or created by you.",
		myTicketsAllStatuses: "All",
		filtersLabel: "Task filters",
		filterProperty: "Property",
		filterProject: "Project",
		filterAssignee: "Assignee",
		filterTopic: "Topic",
		filterKind: "Type",
		filterAllProperties: "All properties",
		filterAllProjects: "All projects",
		filterAllAssignees: "All assignees",
		filterAllTopics: "All topics",
		filterAllKinds: "All types",
		filterClear: "Clear",
		statusCountLabel: "Status {status}: {count}",
		topicUngrouped: "No topic",
		topicManage: "Manage topics",
		topicCreate: "Add topic",
		topicRename: "Rename",
		topicDelete: "Delete",
		topicDeleteConfirm: "Delete this topic?",
		topicNameLabel: "Topic name",
		topicNamePlaceholder: "e.g. Kitchen",
		topicScopeLabel: "Scope",
		topicScopeProperty: "Whole property",
		topicCreateError: "Couldn't add topic",
		topicUpdateError: "Couldn't update topic",
		topicDeleteError: "Couldn't delete topic",
		status: {
			open: "Open",
			in_progress: "In progress",
			done: "Done",
			cancelled: "Cancelled",
		},
		kind: {
			task: "Task",
			defect: "Defect",
		},
		formErrors: {
			titleRequired: "Enter a task title",
			titleTooLong: "Title can be at most {max} characters",
			descriptionTooLong: "Description can be at most {max} characters",
			unknown: "Couldn't save the task",
		},
	},

	userMainRoles: {
		investor: "Investor",
		contractor: "Contractor",
		architect: "Architect",
	},

	apiErrors: {
		auth: {
			unauthorized: "Unauthorized",
			forbidden: "You do not have permission to do this",
			onboardingRequired: "Onboarding required",
			emailRequired: "Email required",
			emailAlreadyAssociated:
				"Email already associated with a different account",
		},
		validation: {
			failed: "Validation failed",
		},
		user: {
			notSynced: "User not found. Call /api/common/auth/sync first.",
		},
		notifications: {
			notFound: "Notification not found",
			cursorInvalid: "Invalid page cursor — start from the beginning",
		},
		property: {
			notFound: "Property not found",
			forbidden: "You do not have access to this property",
			transferConfirmationMismatch:
				"The confirmation name doesn't match. Type the exact property name.",
			transferSelf: "You can't transfer the property to yourself.",
			transferNotAMember:
				"The selected user is not a member of this property anymore.",
		},
		projects: {
			notFound: "Project not found",
			forbidden: "You do not have permission to do this in this project",
			constructionRequiresHouse:
				"Construction projects can only be created for houses",
			statusAcceptedOwnerOnly:
				"Only the property owner can mark a project as accepted",
			memberNotEligible: "This user can't be added to the project",
			memberAlreadyAdded: "This user is already a project member",
			memberNotFound: "Project member not found",
			proposalNotFound: "Cost proposal not found",
			proposalAlreadyDecided: "This cost proposal has already been decided",
			proposalConflict:
				"The cost proposal just changed. Refresh and try again.",
			proposalsLockedPaid:
				"The project is already paid — cost proposals are locked",
			paidRequiresAcceptedCost:
				"Accept a cost proposal before marking the project as paid",
			alreadyPaid: "The project is already marked as paid",
		},
		agentActions: {
			notFound: "Action not found",
			alreadyResolved: "This action has already been resolved",
			expired: "This action has expired",
		},
		inspirations: {
			notFound: "Inspiration not found",
			forbidden: "You do not have permission to do this",
			unsupportedContentType: "This file type is not supported",
			fileTooLarge: "This file is too large",
			quotaExceeded: "This project has reached its inspirations limit",
			uploadUrlFailed: "Could not start the upload. Please try again",
			uploadNotPending: "This upload is no longer pending",
			uploadObjectMissing: "The uploaded file could not be found",
			uploadInvalid: "The uploaded file is invalid",
			storageError: "Storage is temporarily unavailable. Please try again",
			noFields: "Nothing to update",
		},
		visualizations: {
			notFound: "Visualization not found",
			forbidden: "You do not have permission to do this",
			unsupportedContentType: "This file type is not supported",
			fileTooLarge: "This file is too large",
			quotaExceeded: "This project has reached its visualizations limit",
			uploadUrlFailed: "Could not start the upload. Please try again",
			uploadNotPending: "This upload is no longer pending",
			uploadObjectMissing: "The uploaded file could not be found",
			uploadInvalid: "The uploaded file is invalid",
			storageError: "Storage is temporarily unavailable. Please try again",
			noFields: "Nothing to update",
		},
		invitation: {
			notFound: "Invitation not found",
			alreadyAccepted: "Invitation already accepted",
			expired: "Invitation expired",
			selfInvite: "Cannot invite yourself — you are the owner",
			alreadyMember: "User is already a member",
			emailMismatch:
				"This invitation was sent to {email}. Sign in with that email to accept.",
			wrongPin: "Wrong PIN. Ask the inviter to show the code again.",
			resendQrUnsupported: "QR invitations can't be resent.",
		},
		membership: {
			alreadyMember: "You are already a member",
			notFound: "Membership not found",
			cannotRemoveOwner: "The property owner cannot be removed",
			cannotChangeOwnerRole: "Cannot change the owner's role",
		},
		realtime: {
			channelForbidden: "You don't have access to this channel.",
			unsupportedChannel: "Unsupported channel.",
		},
		chat: {
			forbidden: "You don't have access to this chat.",
			acknowledgmentInvalid: "The acknowledgment is invalid.",
			participantRequired: "You're not a participant of this chat.",
		},
		fcm: {
			tokenNotFound: "Notification token not found.",
		},
		journal: {
			cursorInvalid: "The journal cursor is invalid or malformed.",
			limitOutOfRange: "Journal limit must be between 1 and 100.",
		},
		tickets: {
			notFound: "Ticket not found",
			forbidden: "You do not have permission to do this with this ticket",
			projectScopeMismatch: "That project doesn't belong to this property",
			topicScopeMismatch: "That topic doesn't belong to this property",
			assigneeNotMember: "You can only assign this to a member of the property",
			invalidTransition: "That status change isn't allowed",
			cursorInvalid: "The ticket cursor is invalid or malformed.",
			limitOutOfRange: "Ticket limit must be between 1 and 100.",
			statusConflict: "The ticket just changed. Refresh and try again.",
		},
		ticketComments: {
			notFound: "Comment not found",
			forbidden: "You do not have permission to delete this comment",
		},
		ticketAttachments: {
			notFound: "Attachment not found",
			forbidden: "You do not have permission to delete this attachment",
			unsupportedContentType: "This file type is not supported",
			fileTooLarge: "This file is too large",
			validationFailed: "The uploaded file is invalid",
		},
		mediaUploads: {
			notFound: "Upload not found",
			forbidden: "You do not have permission to use this upload",
			propertyRequired: "Property is required for this upload",
			unsupportedContentType: "This image type is not supported",
			fileTooLarge: "This image is too large",
			validationFailed: "The uploaded image is invalid",
			objectMissing: "The uploaded image could not be found",
		},
		documents: {
			notFound: "Document not found",
			forbidden: "You do not have permission to do this",
			unsupportedContentType: "This file type is not supported",
			fileTooLarge: "This file is too large",
			validationFailed: "The uploaded file is invalid",
			granteeNotMember: "You can only share this with a member of the property",
			objectMissing: "The uploaded file could not be found",
		},
		topics: {
			notFound: "Topic not found",
			forbidden: "Only property managers can manage topics",
			projectScopeMismatch: "That project isn't part of this property",
		},
		budgetEntries: {
			notFound: "Budget entry not found",
			forbidden: "Only the property owner can manage the budget",
			projectScopeMismatch: "That project doesn't belong to this property",
			topicScopeMismatch: "That topic doesn't belong to this property",
			amountRequired: "An amount is required",
			amountInvalid: "That amount isn't valid",
			invalidStatus: "That status change isn't allowed",
			statusConflict: "The budget entry just changed. Refresh and try again.",
			noFields: "Nothing to update",
			cursorInvalid: "The budget cursor is invalid or malformed.",
			limitOutOfRange: "Budget limit must be between 1 and 100.",
			unsupportedContentType: "That file type isn't supported",
			fileTooLarge: "That file is too large",
			uploadUrlFailed: "Couldn't prepare the upload. Try again.",
			objectMissing: "The uploaded file is missing. Try uploading again.",
			validationFailed: "The uploaded file failed validation",
			storageError: "Storage error. Try again.",
			documentNotReady: "The document isn't ready yet",
		},
	},

	apiPushCopy: {
		chat: {
			titlePrivate: "{property} · Mareczek",
			titleShared: "{property}",
			bodyPreviewFallback: "(empty message)",
		},
		journal: {
			memberInvited: "{actor} invited a new member ({role})",
			memberJoined: "{actor} joined the property",
			propertyCreated: "{actor} created the property",
			projectCreated: "{actor} created project {project}",
			projectStatusChanged: "{actor} moved {project}: {from} → {to}",
			projectCostProposed:
				"{actor} proposed a cost of {amount} {currency} in {project}",
			projectCostAccepted:
				"{actor} accepted a cost of {amount} {currency} in {project}",
			projectCostRejected:
				"{actor} rejected a cost of {amount} {currency} in {project}",
			projectPaid: "{actor} marked {amount} {currency} as paid in {project}",
			projectMemberAdded: "{actor} added {member} to {project}",
			projectMemberRemoved: "{actor} removed {member} from {project}",
			projectArchived: "{actor} archived {project}",
			projectUnarchived: "{actor} restored {project}",
			inspirationCreated: "{actor} added an inspiration to {project}",
			inspirationDeleted: "{actor} removed an inspiration from {project}",
			visualizationCreated: "{actor} added a visualization to {project}",
			visualizationDeleted: "{actor} removed a visualization from {project}",
			documentCreated: "{actor} added a document",
			documentDeleted: "{actor} removed a document",
			documentUpdated: "{actor} updated a document",
		},
		property: {
			joinApproved: "You were added to {property}.",
			joinRequest: "{requester} wants to join the property",
		},
		ticket: {
			created: "{actor} added a task: {title}",
			createdDefect: "{actor} reported a defect: {title}",
			assigned: "{actor} assigned you: {title}",
			statusChanged: "{title}: {status}",
			statusDone: "{actor} completed: {title}",
			statusDoneDefect: "{actor} fixed the defect: {title}",
			reminder: "Reminder: {title}",
			reminderDefect: "Defect reminder: {title}",
			commentMention: "{actor} mentioned you in a comment: {title}",
			commentAdded: "{actor} commented on: {title}",
			mention: "{actor} mentioned you in a ticket: {title}",
		},
	},

	sidebar: {
		navLabel: "Main navigation",
		properties: "Properties",
		myTickets: "My tasks",
		profile: "Profile",
		settings: "Settings",
		signOut: "Sign out",
		more: "More",
		collapseMenu: "Collapse menu",
		expandMenu: "Expand menu",
	},

	settings: {
		title: "Settings",
		notifications: {
			title: "Notifications",
			masterToggle: "Allow notifications",
			quietHoursToggle: "Quiet hours",
			quietHoursStart: "From",
			quietHoursEnd: "To",
			propertiesTitle: "Muted properties",
			muteProperty: "Mute {property}",
			save: "Save notifications",
			savedTimeZone: "Saved timezone",
			loading: "Loading notifications",
			error: "Could not save notification settings.",
		},
	},

	theme: {
		light: "light",
		dark: "dark",
		auto: "auto",
		toggleLabel: "Theme",
	},

	profile: {
		title: "Profile",
		firstNameLabel: "First name",
		firstNamePlaceholder: "Jane",
		lastNameLabel: "Last name",
		lastNamePlaceholder: "Doe",
		languageLabel: "Language",
		aiContextLabel: "AI context",
		aiContextPlaceholder:
			"Short context about you that we'll pass to AI: who you are, what units you work in, what language you expect replies in.",
		mainRoleLabel: "Main role",
		save: "Save",
		cancel: "Cancel",
		saved: "Saved",
		deleteAccount: {
			sectionTitle: "Delete account",
			sectionBody:
				"Deletes your account, personal data, access, and properties you own. Chat messages stay in project history as Deleted user.",
			open: "Delete account",
			dialogTitle: "Delete account?",
			dialogBody:
				"Your account, personal data, owned properties, invitations, and memberships will be removed. Chat messages stay visible as Deleted user. This cannot be undone.",
			confirmationPhrase: "DELETE",
			phraseLabel: "Type {phrase} to confirm",
			confirm: "Delete account",
			submitting: "Deleting...",
			cancel: "Cancel",
			error: "Could not delete the account.",
		},
		errors: {
			firstNameRequired: "First name is required.",
			firstNameTooLong: "Maximum length: {max} characters.",
			lastNameRequired: "Last name is required.",
			lastNameTooLong: "Maximum length: {max} characters.",
			aiContextTooLong: "Maximum length: {max} characters.",
			mainRoleRequired: "Pick a role.",
			unknown: "Could not save profile.",
		},
	},

	onboarding: {
		welcomeHeading: "Welcome to zPlanem!",
		justOneMoreThing: "Just one more thing",
		subtitle: "Tell us about yourself so we can tailor your experience.",
		firstNameLabel: "First name",
		firstNamePlaceholder: "Jane",
		lastNameLabel: "Last name",
		lastNamePlaceholder: "Doe",
		languageLabel: "Language",
		mainRoleLabel: "I am a…",
		mainRolePlaceholder: "Choose your role",
		termsLabel: "I accept the {link}",
		termsLink: "Terms of Service",
		submit: "Get started",
		errors: {
			firstNameRequired: "First name is required.",
			firstNameTooLong: "Maximum length: {max} characters.",
			lastNameRequired: "Last name is required.",
			lastNameTooLong: "Maximum length: {max} characters.",
			mainRoleRequired: "Pick a role.",
			termsRequired: "You must accept the Terms of Service.",
			unknown: "Could not complete onboarding.",
		},
	},
}
